import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";

// A7b (I.160, decisão 3): item achado na prateleira — qualquer contador adiciona produto ativo da empresa;
// a busca e as recusas (já nesta, em outra conferência aberta do depósito) são do banco.

interface FoundProduct {
  id: string;
  name: string;
  sku: string | null;
  ean: string | null;
  category: string | null;
  product_type: string | null;
  already_here: boolean;
  other_open_checklist: string | null;
}

interface StockChecklistAddProductDialogProps {
  token: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Depois de adicionar: a contagem busca o produto para ele aparecer na página */
  onAdded: (product: { name: string; sku: string | null }) => void;
}

export const StockChecklistAddProductDialog = ({ token, open, onOpenChange, onAdded }: StockChecklistAddProductDialogProps) => {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search.trim(), 300);

  const { data: products = [], isFetching } = useQuery({
    queryKey: ["stock-check-found-search", token, debounced],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("search_stock_checklist_products", { p_token: token, p_search: debounced });
      if (error) throw error;
      const result = data as unknown as { success: boolean; error?: string; items?: FoundProduct[] };
      if (!result.success) throw new Error(result.error ?? "Erro na busca");
      return result.items ?? [];
    },
    enabled: open && debounced.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: async (product: FoundProduct) => {
      const { data, error } = await supabase.rpc("add_stock_checklist_item", { p_token: token, p_product_id: product.id });
      if (error) throw error;
      const result = data as unknown as { success: boolean; error?: string };
      if (!result.success) throw new Error(result.error ?? "Erro ao adicionar produto");
      return product;
    },
    onSuccess: (product) => {
      toast.success(`${product.name} adicionado à conferência`);
      setSearch("");
      onOpenChange(false);
      onAdded({ name: product.name, sku: product.sku });
    },
    onError: (err) => {
      toast.error((err as { message?: string })?.message || "Erro ao adicionar produto");
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!addMutation.isPending) { onOpenChange(o); if (!o) setSearch(""); } }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Adicionar produto</DialogTitle>
          <DialogDescription>
            Achou na prateleira um produto que não está na conferência? Busque pelo nome, SKU ou código de barras.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Digite pelo menos 2 letras..."
            className="pl-9"
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-1">
          {debounced.length < 2 ? (
            <p className="text-sm text-muted-foreground py-2">Digite pelo menos 2 letras.</p>
          ) : isFetching ? (
            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Buscando...
            </div>
          ) : products.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Nenhum produto ativo encontrado.</p>
          ) : (
            products.map((p) => {
              const blocked = p.already_here || !!p.other_open_checklist;
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={blocked || addMutation.isPending}
                  onClick={() => addMutation.mutate(p)}
                  className="w-full text-left rounded-md border border-border px-3 py-2 hover:bg-muted disabled:opacity-60 disabled:hover:bg-transparent"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex-1 text-sm font-medium">{p.name}</span>
                    {!blocked && <Plus className="h-4 w-4 text-primary" />}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono">{p.sku ?? "—"}</span>
                    {p.category && <span>{p.category}</span>}
                    {p.already_here && <Badge variant="outline" className="text-[10px]">já nesta conferência</Badge>}
                    {p.other_open_checklist && (
                      <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700 border-amber-500/30">
                        em outra conferência aberta: {p.other_open_checklist}
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
